import type { LensCardData, LensFilterValues } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';
import { type LensDataReadiness, lensDataReady } from '~/utils/leLensTransport';

import { type Row, number } from './liveModel';
import { FILTER_KEYS, currentQuarter, quarterEnd } from './model';
import { OFFER_BATCH_FIELDS } from './offerBatchFields';
import {
  type Filters,
  METRIC_IDS,
  type Model,
  REPORT_VIEWS,
  type ReportView,
  build,
  cardsForView,
  defaults,
  dimensionValues,
  parseReportView,
  selectionValid,
  validQuarter,
} from './presentationModel';
import type { AgentHubOfferRow, AgentHubTeamRow, LiveOptions, MetricRow, OfferRow } from './sourceTypes';

export const filterMap = {
  manager: 'rsm',
  region: 'pcm_region',
  category: 'category',
  person: 'pcm_name',
  rsmRegion: 'rsm_region',
  brand: 'brand_name',
  product: 'product_name',
  country: 'offer_country_name',
  offer: 'offer_bk',
  opportunity: 'offer_opportunity_name',
  offerName: 'offer_name',
  // Bound to bdm_name on agent_hub_offers and pcm_name on targets (workbook "Bdm Name Selection").
  bdm: 'bdm_name',
} as const;
export const todaySydney = (now: Date) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
export const scopeIdentity = (filters: Filters, view: string) =>
  JSON.stringify([view, ...Object.entries(filters).sort(([a], [b]) => a.localeCompare(b))]);
export function validScope(f: Filters, view: string) {
  return (
    validQuarter(f.quarter) &&
    (REPORT_VIEWS as ReadonlyArray<string>).includes(view) &&
    selectionValid(f.selection) &&
    ['Before Refund', 'After Refund'].includes(f.refund) &&
    ['Scorecard', 'Offers'].includes(f.section) &&
    METRIC_IDS.includes(f.metric)
  );
}
export function retainedNativeFilters(search: string): Record<string, Array<string>> {
  const params = new URLSearchParams(search);
  return Object.fromEntries(
    FILTER_KEYS.filter((key) => !Object.values(filterMap).some((mapped) => mapped === key))
      .map((key) => [key, params.getAll('ck_' + key)])
      .filter(([, values]) => values.length),
  );
}
export function requestFilters(
  f: Filters,
  view: string,
  now: Date,
  retained: Record<string, Array<string>> = {},
): LensFilterValues {
  const quarter = validQuarter(f.quarter) ? f.quarter : currentQuarter(now);
  return {
    ...retained,
    period: { window: { from: quarter, to: quarterEnd(quarter) }, comparison: null },
    report_view: [view],
    ...Object.fromEntries(
      Object.entries(filterMap)
        .filter(([key]) => f[key] !== 'All')
        .map(([key, native]) => [native, dimensionValues(f[key])]),
    ),
  };
}
export function readLiveFilters(search: string, now: Date): Filters {
  const p = new URLSearchParams(search),
    f = defaults(now);
  const aliases: Record<string, string> = { ...filterMap, refund: 'basis' };
  for (const key of Object.keys(f) as Array<keyof Filters>) {
    const legacy = p.getAll('ck_' + (aliases[key] ?? key));
    if ((key === 'metric' || key === 'section') && p.has('ck_' + key)) {
      f[key] = p.get('ck_' + key)!;
    } else if (p.has(key)) {
      f[key] = p.get(key)!;
    } else if (legacy.length) {
      f[key] =
        key === 'refund'
          ? legacy[0] === 'BR'
            ? 'Before Refund'
            : 'After Refund'
          : legacy.length > 1
            ? JSON.stringify(legacy)
            : legacy[0];
    }
  }
  const selected = p.getAll('ck_owner_tuple');
  if (!p.has('selection') && selected.length) {
    try {
      f.selection = JSON.stringify(
        selected.map((value) => {
          const row = object(value);
          return { person: dim(row, 'pcm_name'), region: dim(row, 'pcm_region'), rsmRegion: dim(row, 'rsm_region') };
        }),
      );
    } catch {
      f.selection = 'invalid';
    }
  }
  return f;
}

export function readLiveView(params: URLSearchParams): ReportView {
  return parseReportView(params.get('ck_view') ?? params.get('view'));
}

/** Workspace routing owns view/metric/section; app state must survive nativePageParams. */
export function writeLiveFilters(
  current: URLSearchParams,
  patch: Partial<Filters>,
  view: string,
  now: Date,
): URLSearchParams {
  const next = new URLSearchParams(current);
  const filters = { ...readLiveFilters(current.toString(), now), ...patch };
  for (const [key, value] of Object.entries(filters)) {
    // An untouched current-quarter default stays relative when another control changes.
    if (key === 'quarter' && !('quarter' in patch) && !current.has('quarter') && !current.has('ck_quarter')) {
      continue;
    }
    const name = key === 'metric' || key === 'section' ? 'ck_' + key : key;
    next.set(name, value);
  }
  next.set('ck_view', view);
  for (const key of ['view', 'metric', 'section', 'offerPage']) {
    next.delete(key);
  }
  return next;
}
function object(value: unknown): Row {
  const parsed: unknown = typeof value === 'string' ? JSON.parse(value) : value;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Malformed owner/offer identity.');
  }
  return parsed as Row;
}
function dim(row: Row, key: string): string | null {
  const value = row[key];
  if (value === null) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  throw new Error('Missing or invalid identity field: ' + key);
}
function operand(row: Row, key: string): number | null {
  if (!(key in row)) {
    throw new Error('Missing operand: ' + key);
  }
  if (row[key] === null) {
    return null;
  }
  const n = number(row[key]);
  if (n === null) {
    throw new Error('Invalid operand: ' + key);
  }
  return n;
}
/** Restore the full counted offer population before any owner grouping, totals or CSV. */
function decodeOffers(batches: Array<Row>): Array<Row> {
  const decoded: Array<Row> = [];
  const indexes = new Set<number>();
  let expected: number | undefined;
  const integer = (value: unknown): number => {
    if (
      !(
        (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) &&
        Number.isSafeInteger(Number(value))
      )
    ) {
      throw new Error('Invalid offers batch integer.');
    }
    return Number(value);
  };
  for (const batch of batches) {
    const index = integer(batch.batch),
      count = integer(batch.record_count),
      total = integer(batch.source_count);
    if (
      index < 0 ||
      indexes.has(index) ||
      count < 1 ||
      count > 500 ||
      total < count ||
      (expected !== undefined && total !== expected)
    ) {
      throw new Error('Invalid offers batch population.');
    }
    indexes.add(index);
    expected = total;
    if (typeof batch.payload !== 'string') {
      throw new Error('Missing offers batch payload.');
    }
    const tuples: unknown = JSON.parse(batch.payload);
    if (!Array.isArray(tuples) || tuples.length !== count) {
      throw new Error('Invalid offers batch length.');
    }
    for (const tuple of tuples) {
      if (
        !Array.isArray(tuple) ||
        tuple.length !== OFFER_BATCH_FIELDS.length ||
        typeof tuple[0] !== 'string' ||
        typeof tuple[1] !== 'string'
      ) {
        throw new Error('Invalid offers source tuple.');
      }
      for (const value of tuple.slice(2)) {
        if (value !== null && !(typeof value === 'number' && Number.isFinite(value))) {
          throw new Error('Invalid offers source operand.');
        }
      }
      decoded.push(Object.fromEntries(OFFER_BATCH_FIELDS.map((field, index) => [field, tuple[index]])));
    }
  }
  if (decoded.length !== (expected ?? 0) || [...indexes].some((index) => index >= indexes.size)) {
    throw new Error('Incomplete offers source population.');
  }
  return decoded;
}
function rows(cards: ReadonlyArray<LensCardData>, id: string, keys: Array<string>): Array<Row> {
  const card = cards.find((c) => c.cardId === id);
  if (!card) {
    throw new Error('Missing ' + id + ' card.');
  }
  const transport = card.rows ?? [];
  const data = id === 'offers' ? decodeOffers(transport) : transport;
  if (
    (card.status === 'ready' && !data.length) ||
    (card.status === 'empty' && data.length) ||
    (card.totalRowCount !== undefined && card.totalRowCount > transport.length)
  ) {
    throw new Error('Incomplete ' + id + ' population.');
  }
  const identities = new Set<string>();
  for (const row of data) {
    const key = JSON.stringify(keys.map((k) => row[k]));
    if (identities.has(key)) {
      throw new Error('Duplicate ' + id + ' aggregate grain.');
    }
    identities.add(key);
  }
  return data;
}
function target(row: Row, quarter: string): MetricRow {
  const tuple = object(row.owner_tuple);
  return {
    quarter_start_date: quarter,
    pcm_name: dim(tuple, 'pcm_name'),
    pcm_region: dim(tuple, 'pcm_region'),
    rsm_region: dim(tuple, 'rsm_region'),
    metric_name: dim(row, 'metric_name') ?? 'Unknown',
    actual_value: operand(row, 'actual_value'),
    target_value: operand(row, 'target_value'),
    override_value: operand(row, 'override_value'),
  };
}
function offer(row: Row, quarter: string): OfferRow {
  const mark = object(row.offer_mark);
  const tuple = object(row.owner_tuple);
  for (const key of ['pcm_name', 'pcm_region', 'rsm_region']) {
    if (dim(mark, key) !== dim(tuple, key)) {
      throw new Error('Offer and owner identities disagree.');
    }
  }
  return {
    sourceMark: mark,
    quarter_start_date: quarter,
    refunds_adjusted_paired: operand(row, 'refunds_adjusted_paired'),
    lp_addback_br_paired: operand(row, 'lp_addback_br_paired'),
    lp_addback_ar_paired: operand(row, 'lp_addback_ar_paired'),
    pcm_name: dim(mark, 'pcm_name'),
    pcm_region: dim(mark, 'pcm_region'),
    rsm: dim(mark, 'rsm'),
    rsm_region: dim(mark, 'rsm_region'),
    category: dim(mark, 'category'),
    brand_name: dim(mark, 'brand_name'),
    product_name: dim(mark, 'product_name'),
    offer_opportunity_name: dim(mark, 'offer_opportunity_name'),
    offer_bk: dim(mark, 'offer_bk'),
    offer_name: dim(mark, 'offer_name'),
    offer_start_date: dim(mark, 'offer_start_date'),
    offer_end_date: dim(mark, 'offer_end_date'),
    offer_country_name: dim(mark, 'offer_country_name'),
    salesperson_margin_forecast: dim(mark, 'salesperson_margin_forecast'),
    gm_ar: operand(row, 'gm_ar'),
    gm_br: operand(row, 'gm_br'),
    gm_adjusted_ar: operand(row, 'gm_adjusted_ar'),
    gm_adjusted_br: operand(row, 'gm_adjusted_br'),
    ttv_ar: operand(row, 'ttv_ar'),
    ttv_br: operand(row, 'ttv_br'),
    ttv_adjusted_ar: operand(row, 'ttv_adjusted_ar'),
    ttv_adjusted_br: operand(row, 'ttv_adjusted_br'),
    net_margin_adjusted: operand(row, 'net_margin_adjusted'),
    promo_used_aud: operand(row, 'promo_used_aud'),
    lp_margin_add_back_ar: operand(row, 'lp_margin_add_back_ar'),
    ics_cost_margin_reduction_aud: operand(row, 'ics_cost_margin_reduction_aud'),
    pcm_tour_addback: operand(row, 'pcm_tour_addback'),
    ten_percent_pcm_tour_margin_reduction: operand(row, 'ten_percent_pcm_tour_margin_reduction'),
    lp_margin_loss_sale: operand(row, 'lp_margin_loss_sale'),
    lp_margin_gain_sale: operand(row, 'lp_margin_gain_sale'),
    lp_margin_loss_refund: operand(row, 'lp_margin_loss_refund'),
    lp_margin_gain_refund: operand(row, 'lp_margin_gain_refund'),
  };
}
function teamRow(row: Row): AgentHubTeamRow {
  return {
    metric_name: dim(row, 'metric_name') ?? 'Unknown',
    value_basis: dim(row, 'value_basis'),
    actual_value: operand(row, 'actual_value'),
    target_value: operand(row, 'target_value'),
    bdm_count: operand(row, 'bdm_count'),
    bdm_value_variants: operand(row, 'bdm_value_variants'),
    offers_gm_adjusted_ar: operand(row, 'offers_gm_adjusted_ar'),
  };
}
function agentHubOffer(row: Row, index: number): AgentHubOfferRow {
  const mark = object(row.offer_mark);
  if (dim(mark, 'bdm_name') !== dim(row, 'bdm_name')) {
    throw new Error('Agent Hub offer and BDM identities disagree.');
  }
  return {
    key: String(index),
    bdm_name: dim(mark, 'bdm_name'),
    offer_country_name: dim(mark, 'offer_country_name'),
    brand_name: dim(mark, 'brand_name'),
    product_name: dim(mark, 'product_name'),
    offer_bk: dim(mark, 'offer_bk'),
    offer_name: dim(mark, 'offer_name'),
    ttv_adjusted_br: operand(row, 'ttv_adjusted_br'),
    refund_adjusted: operand(row, 'refund_adjusted'),
    ttv_adjusted_ar: operand(row, 'ttv_adjusted_ar'),
    gm_adjusted_br: operand(row, 'gm_adjusted_br'),
    gm_adjusted_ar: operand(row, 'gm_adjusted_ar'),
  };
}
export type SettledKpi = {
  ready: boolean;
  identity: string;
  problem: string | null;
  model: Model;
  targetModel: Model;
  forecastAsOf: string;
  sourceAsOf: null;
  requestedScope: LensFilterValues;
};
function optionsFrom(raw: Record<string, Array<string>>, f: Filters, now: Date): LiveOptions {
  // Calendar choices are civil quarter boundaries, not a claim that a source row exists.
  const current = currentQuarter(now),
    calendar: Array<string> = [];
  for (let offset = 0; offset < 16; offset++) {
    const date = new Date(current + 'T00:00:00Z');
    date.setUTCMonth(date.getUTCMonth() - offset * 3);
    calendar.push(date.toISOString().slice(0, 10));
  }
  return {
    rsmRegions: raw.rsm_region ?? [],
    opportunities: raw.offer_opportunity_name ?? [],
    offerNames: raw.offer_name ?? [],
    quarters: [...new Set([f.quarter, ...calendar])],
    managers: raw.rsm ?? [],
    regions: raw.pcm_region ?? [],
    categories: raw.category ?? [],
    people: raw.pcm_name ?? [],
    brands: raw.brand_name ?? [],
    products: raw.product_name ?? [],
    countries: raw.offer_country_name ?? [],
    bdms: raw.bdm_name ?? [],
  };
}
export function adaptKpi(
  f: Filters,
  view: string,
  input: LensDataReadiness,
  rawOptions: Record<string, Array<string>>,
  now: Date,
  requestedScope: LensFilterValues = requestFilters(f, view, now),
): SettledKpi {
  const today = todaySydney(now),
    options = optionsFrom(rawOptions, f, now),
    empty = { metrics: [], offers: [] };
  const result: SettledKpi = {
    ready: false,
    identity: scopeIdentity(f, view),
    problem: null,
    model: build(empty, f, view, today, options),
    targetModel: build(empty, { ...f, selection: '[]' }, view, today, options),
    forecastAsOf: today,
    sourceAsOf: null,
    requestedScope,
  };
  if (!validScope(f, view)) {
    result.problem = 'Choose a valid calendar quarter, metric, refund basis and owner selection.';
    return result;
  }
  if (!lensDataReady(input, cardsForView(view))) {
    result.problem = 'Waiting for complete live data for this scope.';
    return result;
  }
  try {
    const cards = input.cards ?? [],
      agentHub = view === 'Agent Hub',
      population = {
        metrics: rows(cards, 'targets', ['owner_tuple', 'metric_name']).map((row) => target(row, f.quarter)),
        offers: agentHub
          ? []
          : rows(cards, 'offers', ['offer_mark', 'owner_tuple']).map((row) => offer(row, f.quarter)),
        team: agentHub ? rows(cards, 'agent_hub_team', ['metric_name', 'value_basis']).map(teamRow) : [],
        agentHubOffers: agentHub ? rows(cards, 'agent_hub_offers', ['offer_mark', 'bdm_name']).map(agentHubOffer) : [],
      };
    result.model = build(population, f, view, today, options);
    result.targetModel = build(population, { ...f, selection: '[]' }, view, today, options);
    result.ready = true;
  } catch (error) {
    result.problem = error instanceof Error ? error.message : 'Incomplete Commercial KPI response.';
  }
  return result;
}
export function exportKpi(rows: Array<Row>, f: Filters, view: string, result: SettledKpi): string {
  if (!result.ready || !validScope(f, view) || scopeIdentity(f, view) !== result.identity) {
    throw new Error('Commercial KPI data is not ready for export.');
  }
  const columns = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  const scope = [
    'Scope',
    'report=Commercial KPI Tracker',
    'view=' + view,
    ...Object.entries(f).map(([key, value]) => key + '=' + value),
    'forecastAsOf=' + result.forecastAsOf,
    'effectiveRequest=' + JSON.stringify(result.requestedScope),
    'sourceAsOf=Unknown',
  ];
  return (
    '\uFEFF' +
    [scope, columns, ...rows.map((row) => columns.map((column) => row[column]))]
      .map((row) => row.map((v) => csvCell(v, 'Unknown')).join(','))
      .join('\r\n') +
    '\r\n'
  );
}
